import AsyncStorage from '@react-native-async-storage/async-storage';

import { useAsyncStorage } from '@sg/garnish';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';

/**
 * Sets the order id in local storage after payment intent creation.
 * This is needed because the cart might no longer exist if the order was already fulfilled.
 * For gift card orders, we also store the gift card slug to be able to open the correct form afterwards.
 */
export const storePendingPaymentInfo = async (
  orderId = '',
  orderType: (typeof OrderTypeValues)[keyof typeof OrderTypeValues],
  giftCardAssetId?: string,
) => {
  const value = JSON.stringify({ orderId, orderType, giftCardAssetId });
  await AsyncStorage.setItem(OrderPaymentStateKey, value);
};

/**
 * Retrieves the order id from local storage.
 * This is needed because the cart might no longer exist if the order was already fulfilled.
 */
export const getLocalStorageOrderId = async () => {
  const value = await AsyncStorage.getItem(OrderPaymentStateKey);
  return parseStoredPendingPaymentInfo(value);
};

/**
 * Clears the order id from local storage.
 * This is triggered after the order payment state has been resolved (fulfilled or failed).
 */
export const clearLocalStorageOrderId = () => {
  void AsyncStorage.removeItem(OrderPaymentStateKey);
};

/**
 * Helper to read and split the order id and order type from local storage.
 */
export const useOrderPaymentStateFromLocalStorage = () => {
  const [storedValue, _, refetchOrderId] = useAsyncStorage(OrderPaymentStateKey);
  const { orderId, orderType, giftCardAssetId } = parseStoredPendingPaymentInfo(storedValue);

  return { orderId, orderType, giftCardAssetId, refetchOrderId };
};

/**
 * Flag order failure as already shown in local storage.
 * When the customer opens the bag, we'll query the order payment state, which includes the error.
 * So this needs to be set when an error occurs because otherwise we'll show it again.
 */
export const setFailureAlreadyShown = async (paymentIntentId = '') => {
  await AsyncStorage.setItem(LastOrderFailureKey, paymentIntentId);
};

/**
 * Checks if the order failure has already been shown for the given payment intent id.
 * Also updates the local storage with the payment intent id so it can be checked against..
 */
export const checkIfFailureWasAlreadyShown = async (paymentIntentId = '') => {
  const lastOrderFailure = await AsyncStorage.getItem(LastOrderFailureKey);

  return lastOrderFailure === paymentIntentId;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseStoredPendingPaymentInfo = (value?: string | null) => {
  if (!value) {
    return { orderId: '', orderType: OrderTypeValues.GravyOrder, giftCardAssetId: undefined };
  }

  // Edge case in which older clients stored the value as an order id string and then updated the app.
  if (Number.isInteger(Number.parseInt(value))) {
    return { orderId: value, orderType: OrderTypeValues.GravyOrder, giftCardAssetId: undefined };
  }

  try {
    const { orderId, orderType, giftCardAssetId } = JSON.parse(value);

    return { orderId, orderType, giftCardAssetId };
  } catch {
    return { orderId: '', orderType: OrderTypeValues.GravyOrder, giftCardAssetId: undefined };
  }
};

// ─── Constants ───────────────────────────────────────────────────────────────

const OrderPaymentStateKey = 'order-payment-state';
const LastOrderFailureKey = 'last-order-failure';
